import { useState, useRef } from "react";
import CaseLookup from "./components/CaseLookup";
import { MonerisProvider } from "./components/MonerisProvider";
import CaseDisplay from "./components/CaseDisplay";
import CaseActionGroup from "./components/CaseActionGroup";
import Payment from "./components/Payment";
import Receipt from "./components/Receipt";
import ExtensionRequest from "./components/ExtensionRequest";
import ScreeningRequest from "./components/ScreeningRequest";
import HearingRequest from "./components/HearingRequest";
import LoadingOverlay from "./components/LoadingOverlay";
import { ConfigProvider } from "./ConfigContext";
import DOMPurify from 'dompurify';

export default function App() {
  const [error, setError] = useState(null);
  const lookupFormRef = useRef(null);
  const queryParameters = new URLSearchParams(window.location.search);
  const [caseData, setCaseData] = useState(undefined);
  const [paymentResponse, setPaymentResponse] = useState(null);
  const [step, setStep] = useState(1);
  const [action, setAction] = useState(null);
  const [requestError, setRequestError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adminView, setAdminView] = useState(
    queryParameters.get("admin") ? Boolean(queryParameters.get("admin")) : false
  );

  const caseNumParam = queryParameters.get("case-num") || "";
  const plateParam = queryParameters.get("plate") || "";
  const actionParam = queryParameters.get("action") || "";

  const onCaseLookup = (theCaseData) => {
    setCaseData(theCaseData);
    setAction(null);
    setPaymentResponse(null);
    if (theCaseData) {
      let scrollTo = "step2";
      setStep(2);
      if (
        theCaseData[
          "apt_casestatus@OData.Community.Display.V1.FormattedValue"
        ] === "Paid"
      ) {
        setStep(4);
        scrollTo = "step3";
        setAction("Paid in Full");
      } else if (
        theCaseData[
          "apt_casestatus@OData.Community.Display.V1.FormattedValue"
        ] === "Set Aside"
      ) {
        setStep(4);
        scrollTo = "step3";
        setAction("Set Aside");
      } else if (actionParam) {
        switch (actionParam) {
          case "pay":
            onPayTrigger();
            break;

          default:
            break;
        }
      }
      setTimeout(() => {
        let element = document.getElementById(scrollTo);
        element.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };

  const onReset = () => {
    setAction(null);
    setStep(1);
    setCaseData(undefined);
  };

  // PAYMENT
  const onPayTrigger = () => {
    setPaymentResponse(null);
    setLoading("Preparing transaction...");
    setAction("Pay Penalty");
    setStep(3);
  };

  const onPayLoaded = () => {
    setLoading(false);
    let element = document.getElementById("step3");
    element.scrollIntoView({ behavior: "smooth" });
  };

  const onPaymentCancelled = () => {
    setAction(null);
    setStep(2);
  };

  const onPaymentApproved = (transactionResponse) => {
    setPaymentResponse(transactionResponse);
    setAction("Payment Receipt");
    setStep(4);
    // update local data about case because workflow on backend is a bit delayed
    let totalPayableUpdated =
      caseData.apt_totaladjustedfines -
      (caseData.apt_totalpayments + transactionResponse.apt_amount);
    let modifiedCaseData = caseData;
    modifiedCaseData.apt_totalpayments =
      caseData.apt_totalpayments + transactionResponse.apt_amount;
    if (totalPayableUpdated === 0) {
      caseData["apt_casestatus@OData.Community.Display.V1.FormattedValue"] =
        "Paid";
      caseData.apt_validforpayment = false;
      caseData.apt_validforextension = false;
      caseData.apt_validforscreening = false;
    }
    setCaseData(modifiedCaseData);
  };

  const onPaymentDeclined = (transactionResponse) => {
    setPaymentResponse(transactionResponse);
    setAction("Payment Receipt");
  };

  const onPaymentError = () => {
    setLoading(false);
  }
  // END PAYMENT

  // EXTENSION
  const onExtensionTrigger = () => {
    setPaymentResponse(null);
    setStep(3);
    setAction("Screening Extension Request");
    setTimeout(() => {
      let element = document.getElementById("step3");
      element.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const onExtensionRequestComplete = () => {
    setRequestError(null);
    setStep(4);
    setAction("Screening Extension Request Submitted");
    // update local data about case because workflow on backend is a bit delayed
    let modifiedCaseData = caseData;
    caseData["apt_casestatus@OData.Community.Display.V1.FormattedValue"] =
      "Extension Review";
    caseData.apt_validforextension = false;
    caseData.apt_validforscreening = false;
    setCaseData(modifiedCaseData);
  };
  // END EXTENSION

  // SCREENING
  const onScreeningTrigger = () => {
    setPaymentResponse(null);
    setStep(3);
    setAction("Screening Review Request");
    setTimeout(() => {
      let element = document.getElementById("step3");
      element.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const onScreeningRequestComplete = () => {
    setRequestError(null);
    setStep(4);
    setAction("Screening Request Submitted");
    // update local data about case because workflow on backend is a bit delayed
    let modifiedCaseData = caseData;
    caseData["apt_casestatus@OData.Community.Display.V1.FormattedValue"] =
      "Screening Review";
    caseData.apt_validforextension = false;
    caseData.apt_validforscreening = false;
    setCaseData(modifiedCaseData);
  };
  // END SCREENING

  // HEARING
  const onHearingTrigger = () => {
    setPaymentResponse(null);
    setStep(3);
    setAction("Hearing Review Request");
    setTimeout(() => {
      let element = document.getElementById("step3");
      element.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const onHearingRequestComplete = () => {
    setRequestError(null);
    setStep(4);
    setAction("Hearing Request Submitted");
    // update local data about case because workflow on backend is a bit delayed
    let modifiedCaseData = caseData;
    caseData["apt_casestatus@OData.Community.Display.V1.FormattedValue"] =
      "Hearing Review";
    caseData.apt_validforhearing = false;
    setCaseData(modifiedCaseData);
  };
  // END HEARING

  return (
    <div>
      <p id="version" hidden>
            {VERSION}
            </p>
    <ConfigProvider>
      <MonerisProvider>
        <div>
          <p id="version" hidden>
            {VERSION}
          </p>
          {/* { !adminView && <h1>My Penalty</h1> } */}
          {adminView && (
            <h2 style={{ color: "red" }}>
              Internal Staff Penalty Payment Portal
            </h2>
          )}
          <ul className="main-steps">
            <li
              id="step1"
              data-step-number="1"
              className={`${step === 1 && "active"} ${step > 1 && "complete"}`}
            >
              <h2>Lookup</h2>
              <p>
                Lookup your penalty order in our system using key pieces of information.
              </p>
              <p>
                Enter the following information exactly as it appears on the penalty
                order.
              </p>
              <CaseLookup
                onCaseLookup={onCaseLookup}
                onReset={onReset}
                caseNumParam={caseNumParam}
                plateParam={plateParam}
                ref={lookupFormRef}
              />
              {/* {caseData && <CaseRawData caseData={caseData}/>} */}
            </li>
            <li
              id="step2"
              data-step-number="2"
              className={`${step === 2 && "active"} ${step > 2 && "complete"}`}
            >
              <h2>Review</h2>
              {caseData && (
                <>
                  <p>
                    The penalty order was found and the details are below. Please
                    review the details before taking any action.
                  </p>
                  <CaseDisplay caseData={caseData} />
                  <CaseActionGroup
                    action={action}
                    caseData={caseData}
                    onPayTrigger={onPayTrigger}
                    onExtensionTrigger={onExtensionTrigger}
                    onScreeningTrigger={onScreeningTrigger}
                    onHearingTrigger={onHearingTrigger}
                  />
                </>
              )}
            </li>
            <li
              id="step3"
              data-step-number="3"
              className={`${step === 3 && "active"} ${step > 3 && "complete"}`}
            >
              <h2>
                Action {action && "-"} {action}
              </h2>
              {caseData && (
                <>
                  {action === "Pay Penalty" && (
                    <Payment
                      caseData={caseData}
                      onPaymentCancelled={onPaymentCancelled}
                      onPaymentApproved={onPaymentApproved}
                      onPaymentDeclined={onPaymentDeclined}
                      onPaymentError={onPaymentError}
                      onPayLoaded={onPayLoaded}
                      adminView={adminView}
                    />
                  )}
                  {paymentResponse && (
                    <>
                      <Receipt caseData={caseData} payment={paymentResponse} />
                      <button
                        className="webform-flex--1 webform-button--submit button button--primary js-form-submit form-submit"
                        onClick={() => {lookupFormRef.current.resetForm(); window.location.href = drupalSettings.apt_config.close_form_url;}}
                      >
                        Close Form
                      </button>
                    </>
                    // <pre>{JSON.stringify(paymentResponse, null, 2)}</pre>
                  )}
                  {action === "Paid in Full" && (
                    <>
                      <p>
                        Penalty Order fully paid and closed on{" "}
                        {
                          caseData[
                            "apt_datefullypaid@OData.Community.Display.V1.FormattedValue"
                          ]
                        }
                        
                      </p>
                      <button
                        className="webform-flex--1 webform-button--submit button button--primary js-form-submit form-submit"
                        onClick={() => {lookupFormRef.current.resetForm(); window.location.href = drupalSettings.apt_config.close_form_url;}}
                      >
                        Close Form
                      </button>
                    </>
                  )}
                  {action === "Screening Extension Request" && (
                    <ExtensionRequest
                      caseData={caseData}
                      onExtensionRequestComplete={onExtensionRequestComplete}
                    />
                  )}
                  {action === "Screening Extension Request Submitted" && (
                    <>
                      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(drupalSettings.apt_config.screening_ext_submit) }} />
                      <button
                        className="webform-flex--1 webform-button--submit button button--primary js-form-submit form-submit"
                        onClick={() => {lookupFormRef.current.resetForm(); window.location.href = drupalSettings.apt_config.close_form_url;}}
                      >
                        Close Form
                      </button>
                    </>
                  )}
                  {action === "Screening Review Request" && (
                    <ScreeningRequest
                      caseData={caseData}
                      onScreeningRequestComplete={onScreeningRequestComplete}
                    />
                  )}
                  {action === "Screening Request Submitted" && (
                    <>
                      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(drupalSettings.apt_config.screening_submit) }} />
                      <button
                        className="webform-flex--1 webform-button--submit button button--primary js-form-submit form-submit"
                        onClick={() => {lookupFormRef.current.resetForm(); window.location.href = drupalSettings.apt_config.close_form_url;}}
                      >
                        Close Form
                      </button>
                    </>
                  )}
                  {action === "Hearing Review Request" && (
                    <HearingRequest
                      caseData={caseData}
                      onHearingRequestComplete={onHearingRequestComplete}
                    />
                  )}
                  {action === "Hearing Request Submitted" && (
                    <>
                      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(drupalSettings.apt_config.hearing_submit) }} />
                      <button
                        className="webform-flex--1 webform-button--submit button button--primary js-form-submit form-submit"
                        onClick={() => {lookupFormRef.current.resetForm(); window.location.href = drupalSettings.apt_config.close_form_url;}}
                      >
                        Close Form
                      </button>
                    </>  
                  )}
                </>
              )}
            </li>
          </ul>
          {loading && <LoadingOverlay label={loading} />}
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(drupalSettings.apt_config.footer) }} />
        </div>
      </MonerisProvider>
    </ConfigProvider>
    {/* TODO: add phone number */}
    <p className="apt-footer">APT Customer Portal - Version {VERSION}</p>
    </div>
  );
}
